import { useEffect, useMemo, useState } from 'react';
import { workspacePanelIsQueue } from '../../panels/workspace-queue-exists';
import { BoloQueueRowData } from '../../socket/stream-types';
import { QueueStore } from '../../queues/store/queue-store';
import { BoloQueueView } from '@mark43/cad-api';
import { autorun } from 'mobx';
import { FilterDirective, ComparatorReturns, SortingDirective } from '@list-store';
import { filterQueueViewsBySearchValue } from '../../queues/queue-filter-helpers';
import { useWorkspacePanel } from '../../panels/hooks/use-workspace-panel';
import { BoloQueueViewsListener, useBoloQueueViews } from './bolo-queue-views-provider';
import { useDispatchAreaStore } from '../../dispatch-areas/use-dispatch-areas';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useSyncWorkspacePanelToQueueStore } from '../../queues/use-sync-workspace-panel-to-queue-store';

function filterByDispatchAreas(boloQueueView: BoloQueueView, dispatchAreaIds: number[]) {
    if (dispatchAreaIds.length === 0) {
        return true;
    }
    return boloQueueView.dispatchAreas.some((dispatchArea) =>
        dispatchAreaIds.includes(dispatchArea.value)
    );
}

function filterBySubdivisions(boloQueueView: BoloQueueView, subdivisionIds: number[]) {
    if (subdivisionIds.length === 0) {
        return true;
    }
    return boloQueueView.subdivisions.some((subdivision) =>
        subdivisionIds.includes(subdivision.value)
    );
}

function createPrioritySortingDirective(): SortingDirective<BoloQueueRowData> {
    return {
        getSortValue: (item) => item.priority,
        comparator: (left: string, right: string) => left.localeCompare(right) as ComparatorReturns,
    };
}

export function createBoloQueueFilterDirective({
    filterQuery,
    dispatchAreaIds,
    subdivisionIds,
}: {
    filterQuery: string;
    dispatchAreaIds: number[];
    subdivisionIds: number[];
}): FilterDirective<BoloQueueView> {
    return (item: BoloQueueView) => {
        return (
            filterBySubdivisions(item, subdivisionIds) &&
            filterByDispatchAreas(item, dispatchAreaIds) &&
            filterQueueViewsBySearchValue(item.searchValue, filterQuery)
        );
    };
}

export function useIntegratedBoloQueueStore(): QueueStore<BoloQueueRowData> {
    const workspacePanel = useWorkspacePanel();
    workspacePanelIsQueue(workspacePanel);
    const { addListener, removeListener, boloQueueViewsRef } = useBoloQueueViews();
    const dispatchAreaStore = useDispatchAreaStore();
    const { isFirstResponder } = useApplicationType();

    const store = useState(
        () => new QueueStore<BoloQueueRowData>({ manualFilterDirective: true, workspacePanel })
    )[0];

    useSyncWorkspacePanelToQueueStore(workspacePanel, store);

    useEffect(() => {
        return autorun(() => {
            if (!isFirstResponder) {
                store.listStore.setFilterDirective(
                    createBoloQueueFilterDirective({
                        filterQuery: store.filterQuery,
                        dispatchAreaIds: dispatchAreaStore.monitoredAndOwnedDispatchAreaIds,
                        subdivisionIds: [],
                    }) as FilterDirective<BoloQueueRowData>
                );
            } else {
                // First responders should be able to see all BOLOs
                store.listStore.setSortingDirective(createPrioritySortingDirective());
            }
        });
    }, [store, dispatchAreaStore, isFirstResponder]);

    const listener: BoloQueueViewsListener = useMemo(() => {
        return {
            onBootstrap() {
                store.setRows(Object.values(boloQueueViewsRef.current));
            },
            onUpdate(bundle) {
                store.upsertRows(bundle.boloQueueViews);
                if (bundle.expiredBoloIds.length) {
                    store.removeRows(bundle.expiredBoloIds);
                }
            },
        };
    }, [store, boloQueueViewsRef]);

    useEffect(() => {
        store.setRows(Object.values(boloQueueViewsRef.current));
        addListener(listener);
        return () => {
            removeListener(listener);
        };
    }, [addListener, boloQueueViewsRef, listener, removeListener, store]);

    return store;
}
